import { dispatchSupportTask } from './supportGateway';
/**
 * AXiM Coding Lab - Enterprise Service Layer (V3)
 * Orchestrates the "Organizational Brain", Multi-Agent Swarms, and Knowledge Injection.
 */
import { supabase } from './supabaseClient';
import { FLEET_REGISTRY } from '../lib/fleetRegistry';

const listeners = new Set();
const broadcast = (event) => listeners.forEach(l => l(event));

// Store local system logs
let SYSTEM_LOGS = JSON.parse(sessionStorage.getItem('axim_system_logs') || '[]');
const addSystemLog = (log) => {
  if (SYSTEM_LOGS.length === 0) SYSTEM_LOGS = JSON.parse(sessionStorage.getItem('axim_system_logs') || '[]');
  SYSTEM_LOGS.push(log);
  if (SYSTEM_LOGS.length > 150) {
    SYSTEM_LOGS = SYSTEM_LOGS.slice(-150);
  }
  sessionStorage.setItem('axim_system_logs', JSON.stringify(SYSTEM_LOGS));
};

const FALLBACK_AGENTS = [
  { id: 'AXIM-CODER-01', name: 'AXiM Coder Core', role: 'Internal Autonomous Engineer', status: 'Active', model: 'Multi-Model (Claude/DeepSeek)', capabilities: ['Ecosystem Tasks', 'PR Sweeping', 'Edge Generation'], uptime: '100%', tasks_completed: 450, isPrimary: true },
  { id: 'ONYX-01', name: 'Onyx Architect', role: 'System Design', status: 'Idle', model: 'DeepSeek-V2-Chat', capabilities: ['AST Parsing', 'Dependency Mapping'], uptime: '99.9%', tasks_completed: 142 },
  { id: 'ASGUARD-01', name: 'Asguard Sentry', role: 'SecOps Patching', status: 'Active', model: 'GPT-4o', capabilities: ['Vulnerability Scan', 'Sanitization'], uptime: '100%', tasks_completed: 89 },
  { id: 'KRONOS-01', name: 'Kronos DevOps', role: 'CI/CD Automation', status: 'Idle', model: 'Claude-3.5', capabilities: ['Wrangler Deploy', 'Workflow Gen'], uptime: '99.8%', tasks_completed: 215 }
];

let CACHED_AGENTS = null;
const fetchAgents = async () => {
  try {
    const { data, error } = await supabase.from('platform_agents').select('*');
    if (error || !data || data.length === 0) return FALLBACK_AGENTS;
    return data;
  } catch (err) {
    console.warn('Failed to fetch platform agents, falling back', err);
    return FALLBACK_AGENTS;
  }
};

// Initialize Realtime Sync
let channel;
let reconnectTimeout = null;
let backoffDelay = 2000;

const connectChannel = () => {
  if (channel) {
    supabase.removeChannel(channel);
  }

  channel = supabase.channel('coding-lab-swarm');

  channel
    .on(
      'postgres_changes',
      { event: '*', schema: 'public', table: 'coding_tasks' },
      async () => {
        const { data } = await supabase.from('coding_tasks').select('*').order('created_at', { ascending: false });
        broadcast({ type: 'TASKS_UPDATED', tasks: data || [] });
      }
    )
    .subscribe((status, err) => {
      if (status === 'SUBSCRIBED') {
        backoffDelay = 2000; // reset on success
      } else if (status === 'CLOSED' || status === 'CHANNEL_ERROR') {
        if (!reconnectTimeout) {
          const log = {
            id: Date.now() + Math.random(),
            text: `[SYSTEM] Connection ${status}. Reconnecting in ${backoffDelay/1000}s...`,
            type: 'system',
            time: new Date().toLocaleTimeString([], { hour12: false })
          };
          addSystemLog(log);
          broadcast({ type: 'LOG_ADDED', log });

          reconnectTimeout = setTimeout(() => {
            reconnectTimeout = null;
            backoffDelay = Math.min(backoffDelay * 2, 16000);
            connectChannel();
          }, backoffDelay);
        }
      }
    });

  return channel;
};

const initializeRealtime = () => {
  return connectChannel();
};

const disconnectRealtime = () => {
  if (channel) {
    supabase.removeChannel(channel);
    channel = null;
  }
  if (reconnectTimeout) {
    clearTimeout(reconnectTimeout);
    reconnectTimeout = null;
  }
};

// Initial setup call
initializeRealtime();

let isRefreshing = false;
let refreshSubscribers = [];

const subscribeTokenRefresh = (cb) => {
  refreshSubscribers.push(cb);
};

const onTokenRefreshed = (err, token) => {
  refreshSubscribers.forEach(cb => cb(err, token));
  refreshSubscribers = [];
};

const getValidToken = async () => {
  const session = await supabase.auth.getSession();
  const expiresAt = session?.data?.session?.expires_at;
  const now = Math.floor(Date.now() / 1000);

  // Buffer if token expires in less than 60s
  if (expiresAt && (expiresAt - now) < 60) {
     if (!isRefreshing) {
        isRefreshing = true;
        try {
           const { data, error } = await supabase.auth.refreshSession();
           isRefreshing = false;
           if (error) throw error;
           onTokenRefreshed(null, data.session.access_token);
           return data.session.access_token;
        } catch (e) {
           isRefreshing = false;
           onTokenRefreshed(e, null);
           return null;
        }
     } else {
        return new Promise((resolve) => {
           subscribeTokenRefresh((err, token) => {
              resolve(token);
           });
        });
     }
  }

  return session?.data?.session?.access_token || localStorage.getItem('axim_internal_key');
};


  let pipelineMetricsCache = {
    dateLabels: [],
    tokenUsage: [],
    nodeHealth: [
          { name: 'Core LLM Proxy', status: 'Unknown', latency: '-', color: 'red' },
          { name: 'GitHub API Bridge', status: 'Unknown', latency: '-', color: 'red' },
          { name: 'Asguard SOC Ingress', status: 'Unknown', latency: '-', color: 'blue' },
          { name: 'Worker Analytics (Edge)', status: 'Unknown', latency: '-', color: 'blue' }
        ],
    roiMetrics: { hoursSaved: 0, efficiencyGain: '0%', totalCost: '$0.00', estimatedSavings: '$0.00' },
    logs: []
  };

export const labService = {

  subscribeToTelemetry: (callback) => {
    let reconnectTimeout = null;
    let telemetryChannel = null;

    const connect = () => {
      if (telemetryChannel) {
        supabase.removeChannel(telemetryChannel);
      }

      telemetryChannel = supabase.channel('green-machine-realtime');
      telemetryChannel
        .on(
          'postgres_changes',
          { event: 'INSERT', schema: 'public', table: 'api_usage_logs' },
          async () => {
             // Dispatch new metrics when api_usage_logs change
             const metrics = await labService.getTelemetryData();
             callback('ONLINE / REALTIME', metrics);
          }
        )
        .subscribe((status) => {
           if (status === 'SUBSCRIBED') {
             callback('ONLINE / REALTIME', null);
           } else if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
             callback('Live sync reconnecting...', null);
             if (!reconnectTimeout) {
               reconnectTimeout = setTimeout(connect, 3000);
             }
           }
        });
    };

    connect();

    return () => {
      if (reconnectTimeout) clearTimeout(reconnectTimeout);
      if (telemetryChannel) supabase.removeChannel(telemetryChannel);
    };
  },

  fetchTelemetryStats: async (timeWindow = '7d') => {
    const cacheKey = 'axim_telemetry_stats_cache';
    const cached = JSON.parse(sessionStorage.getItem(cacheKey) || 'null');
    const ingressUrl = import.meta.env.VITE_INGRESS_URL || '/api/v1/ingress';
    const workerUrl = ingressUrl.replace('/api/v1/ingress', '/api/telemetry/stats') + '?window=' + timeWindow;

    try {
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 3000);

      const res = await fetch(workerUrl, { signal: controller.signal });
      clearTimeout(timeout);

      if (!res.ok) throw new Error('Failed to fetch telemetry stats');
      const stats = await res.json();

      // Cache successful response
      sessionStorage.setItem(cacheKey, JSON.stringify(stats));
      return stats;
    } catch (error) {
      console.warn('Telemetry fetch failed, using local cached averages:', error);
      return cached || { total_events: 0, average_latency: 0, total_tokens: 0, events: [] };
    }
  },

  subscribeToPipelineMetrics: (callback, intervalMs = 3000) => {
    let pollingInterval;
    let eventSource;
    let fallbackMode = false;
    let lastHealthyState = JSON.parse(sessionStorage.getItem('axim_telemetry_cache') || 'null');

    if (lastHealthyState) {
       pipelineMetricsCache = lastHealthyState;
       callback(pipelineMetricsCache, 'OPERATING NORMALLY / CACHED');
    }

    const workerUrl = import.meta.env.VITE_INGRESS_URL ? import.meta.env.VITE_INGRESS_URL.replace('/api/v1/ingress', '/api/telemetry/stream') : '/api/telemetry/stream';

    const startPolling = () => {
      if (pollingInterval) clearInterval(pollingInterval);
      pollingInterval = setInterval(async () => {
        try {
           const metrics = await labService.getTelemetryData();
           pipelineMetricsCache = metrics;
           sessionStorage.setItem('axim_telemetry_cache', JSON.stringify(metrics));
           callback(metrics, 'DEGRADED / FALLBACK');
        } catch (e) {
           callback(pipelineMetricsCache, 'CACHED / OFFLINE');
        }
      }, intervalMs);
    };

    const startStream = () => {
      try {
        eventSource = new EventSource(workerUrl);
        eventSource.onmessage = (event) => {
          fallbackMode = false;
          // In real implementation we would merge this data, here we just invoke the callback
          callback(pipelineMetricsCache, 'ONLINE / REALTIME');
        };
        eventSource.onerror = () => {
          eventSource.close();
          fallbackMode = true;
          startPolling();
        };
      } catch (err) {
         fallbackMode = true;
         startPolling();
      }
    };


    startStream();

    return () => {
      if (eventSource) eventSource.close();
      if (pollingInterval) clearInterval(pollingInterval);
    };
  },

  getValidToken,
  logToConsole(log) {
    addSystemLog(log);
    broadcast({ type: 'LOG_ADDED', log });
  },

  async executeSafely(operation) {
    try {
      const result = await operation();
      return { success: true, data: result || [], error: null };
    } catch (err) {
      console.error('[labService] Operation failed:', err);
      return { success: false, data: null, error: { code: 'NETWORK_ERROR', message: err.message || 'Unknown error' } };
    }
  },


  logAuditEvent: (auditData) => {
    // Non-blocking fire-and-forget telemetry push
    supabase.from('coding_tasks_errors').insert({
      component: auditData.component || 'UI Telemetry',
      message: auditData.action || 'Unknown Action',
      status: auditData.status || 'LOG',
      context: { actor: auditData.actor || 'System', target: auditData.target || 'N/A' },
      task_id: auditData.taskId || null
    }).then(({ error }) => {
      if (error) console.warn('[Telemetry] Failed to dispatch audit log:', error);
    }).catch(() => {});
  },

  disconnectRealtime,
  reconnectRealtime: () => {
    disconnectRealtime();
    setTimeout(() => initializeRealtime(), 500);
  },
  subscribe: (l) => {
    listeners.add(l);
    return () => listeners.delete(l);
  },

  getTasks: async () => {
    try {
      const { data, error } = await supabase.from('coding_tasks').select('*').order('created_at', { ascending: false });
      if (error) console.error('Error fetching tasks:', error);
      return data || [];
    } catch (err) {
      console.error('Exception fetching tasks:', err);
      return [];
    }
  },

  getSystemLogs: () => [...SYSTEM_LOGS],

  getKnowledge: async () => {
    const { data, error } = await supabase.from('knowledge_nodes').select('*').order('created_at', { ascending: false });
    if (error) console.error('Error fetching knowledge:', error);
    return data || [];
  },

  getAgents: async () => {
    if (CACHED_AGENTS) return CACHED_AGENTS;
    CACHED_AGENTS = await fetchAgents();
    return CACHED_AGENTS;
  },
  
  getOrgStats: () => Promise.resolve({
    totalNodes: 142,
    activeSwarmSize: 12,
    prSuccessRate: '94.2%',
    knowledgeSyncFreq: '5m'
  }),

  addNote: async (note) => {
    const newNote = { 
      ...note, 
      lastUpdated: 'Just now', 
      author: 'Admin_Ellars',
      tags: note.tags || []
    };

    const { data, error } = await supabase.from('knowledge_nodes').insert([newNote]).select().single();

    if (error) {
      console.error('Error adding knowledge node:', error);
      return null;
    }
    
    // Auto-log the brain update
    const log = { id: Date.now(), text: `[BRAIN] New knowledge node added: ${newNote.title}`, type: 'knowledge', time: new Date().toLocaleTimeString() };
    addSystemLog(log);
    broadcast({ type: 'LOG_ADDED', log });
    
    const allKnowledge = await labService.getKnowledge();
    broadcast({ type: 'KNOWLEDGE_UPDATED', knowledge: allKnowledge });

    return data;
  },

  deleteNote: async (id) => {
    const { error } = await supabase.from('knowledge_nodes').delete().eq('id', id);
    if (error) {
      console.error('Error deleting knowledge node:', error);
      return false;
    }
    const allKnowledge = await labService.getKnowledge();
    broadcast({ type: 'KNOWLEDGE_UPDATED', knowledge: allKnowledge });
    return true;
  },

  updateNote: async (id, updates) => {
    const { data, error } = await supabase
      .from('knowledge_nodes')
      .update({ ...updates, lastUpdated: 'Just now' })
      .eq('id', id)
      .select().single();
    if (error) {
      console.error('Error updating knowledge node:', error);
      return null;
    }
    const allKnowledge = await labService.getKnowledge();
    broadcast({ type: 'KNOWLEDGE_UPDATED', knowledge: allKnowledge });
    return data;
  },

  logIncident: async (message) => {
    const log = { id: Date.now() + Math.random(), text: `[CRITICAL] ${message}`, type: 'system', time: new Date().toLocaleTimeString([], { hour12: false }) };
    addSystemLog(log);
    broadcast({ type: 'LOG_ADDED', log });

    try {
      await supabase.from('coding_tasks_errors').insert([
        {
          status: 'FAILED',
          message: message,
          context: { actor: 'Asguard_WAF', target: 'axim-core-api' }
        }
      ]);
    } catch (err) {
      console.error('Failed to write incident to database:', err);
    }
  },

  scaffoldProject: async ({ name, type, requirements }) => {
    const payloadBody = JSON.stringify({
      name,
      type,
      requirements,
      origin_source: 'Manual_Dev_Cockpit'
    });
    const ingressUrl = import.meta.env.VITE_INGRESS_URL ? import.meta.env.VITE_INGRESS_URL.replace("/api/v1/ingress", "/api/v1/projects/scaffold") : "http://localhost:8787/api/v1/projects/scaffold";

    const { data: sessionData } = await supabase.auth.getSession();
    const token = sessionData?.session?.access_token || '';
    if (!token) {
      return { success: false, error: 'Operator session required to scaffold a project.' };
    }

    try {
      const response = await fetch(ingressUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        body: payloadBody
      });
      if (response.status !== 202 && response.status !== 200) {
          const text = await response.text();
          throw new Error(`Scaffolding failed: ${text}`);
      }
      const data = await response.json().catch(() => ({ taskId: 'unknown' }));
      return { success: true, taskId: data.taskId };
    } catch (e) {
      console.error(e);
      return { success: false, error: e.message };
    }
  },


  patchTask: async (id, updates) => {
    try {
      const { data, error } = await supabase
        .from('coding_tasks')
        .update(updates)
        .eq('id', id)
        .select();
      if (error) {
        console.error('Error patching task:', error);
        throw error;
      }
      return data;
    } catch (err) {
      console.error('Exception patching task:', err);
      throw err;
    }
  },

  triggerTask: async (payload) => {
    const taskId = payload.task_id || `TASK-${Math.random().toString(36).substring(7).toUpperCase()}`;
    if (!payload.assigned_model) {
      const prefsStr = typeof window !== 'undefined' ? window.localStorage.getItem('axim_lab_preferences') : null;
      if (prefsStr) {
        try {
          const prefs = JSON.parse(prefsStr);
          if (prefs.model) {
            payload.assigned_model = prefs.model;
          }
        } catch (e) {
          console.error('Failed to parse lab preferences:', e);
        }
      }
      if (!payload.assigned_model) {
        payload.assigned_model = typeof window !== 'undefined' ? window.localStorage.getItem('axim_model') || 'deepseek-coder' : 'deepseek-coder';
      }
    }
    broadcast({ type: 'REASONING_START', taskId, prompt: payload.instruction_prompt });

    let agentList = CACHED_AGENTS || FALLBACK_AGENTS;
    let agent;

    if (payload.assigned_model && payload.assigned_model.toLowerCase().includes('claude')) {
      agent = agentList.find(a => a.role === 'CI/CD Automation' || a.role === 'System Design' || a.name === 'Onyx Architect') || agentList[0];
    } else if (payload.origin_source === 'Asguard_WAF') {
      agent = agentList.find(a => a.role === 'SecOps Patching') || agentList[0];
    } else {
      agent = agentList.find(a => a.name === 'AXiM Coder Core') || agentList[0];
    }
    if (!agent) agent = FALLBACK_AGENTS[0];
    
    const steps = [
      { t: `[INGRESS] Transmitting payload to edge router...`, delay: 100 },
      { t: `[BRAIN] Injecting organizational context: ${payload.contextIds?.length || 0} nodes.`, delay: 800 },
      { t: `[AGENT] Assigning ${agent.name} to task.`, delay: 1500 }
    ];

    steps.forEach(step => {
      setTimeout(() => {
        const log = { id: Date.now() + Math.random(), text: step.t, time: new Date().toLocaleTimeString([], { hour12: false }) };
        addSystemLog(log);
        broadcast({ type: 'LOG_ADDED', log });
      }, step.delay);
    });

    try {


      // We wrap the fetch request to our Worker Router
      const payloadBody = JSON.stringify({
          task_id: taskId,
          repository_owner: payload.repository_owner || 'JimEllars',
          repository_name: payload.repository_name,
          target_file_paths: payload.target_file_paths,
          instruction_prompt: payload.instruction_prompt,
          origin_source: payload.origin_source || 'Manual_Dev_Cockpit',
          contextIds: payload.contextIds,
          runtime_env: payload.runtime_env,
          assigned_model: payload.assigned_model
        });

      // The operator's verified Supabase session is the credential for this
      // call. There is deliberately no client-side HMAC: VITE_* values are
      // inlined into the public bundle, so signing here would either fail or
      // publish the shared internal key. Satellite services sign server-side.
      const { data: sessionData } = await supabase.auth.getSession();
      const session = sessionData?.session;
      const token = session?.access_token || '';
      if (!token) {
        throw new Error('Operator session required to dispatch a coding task.');
      }


      const targetUrl = import.meta.env.VITE_INGRESS_URL || '/api/v1/ingress';
      let attempt = 0;
      const maxAttempts = 3;
      let response;

      while (attempt < maxAttempts) {
        try {
          const controller = new AbortController();
          const id = setTimeout(() => controller.abort(), 8000);
          response = await fetch(targetUrl, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Accept': 'text/event-stream',
              Authorization: `Bearer ${token}`
            },
            body: payloadBody,
            signal: controller.signal
          });
          clearTimeout(id);
          if (response.ok || (response.status >= 400 && response.status < 500)) break;
          throw new Error(`HTTP ${response.status}`);
        } catch (e) {
          attempt++;
          if (attempt >= maxAttempts) throw e;
          await new Promise(r => setTimeout(r, 1000 * Math.pow(2, attempt - 1)));
        }
      }

      if (response.headers.get('content-type')?.includes('text/event-stream')) {
         const reader = response.body.getReader();
         const decoder = new TextDecoder();
         let done = false;

         const processStream = async () => {
             try {
                 while (!done) {
                     const { value, done: doneReading } = await reader.read();
                     done = doneReading;
                     if (value) {
                         const chunk = decoder.decode(value, { stream: true });
                         const lines = chunk.split('\n');
                         for (const line of lines) {
                             if (line.startsWith('data: ')) {
                                 const dataStr = line.replace('data: ', '').trim();
                                 if (dataStr && !dataStr.startsWith('{": ping"}') && dataStr !== 'ping' && dataStr !== ': ping') {
                                     try {
                                         const ev = JSON.parse(dataStr);
                                         if (ev.type === 'reasoning_step') {
                                             broadcast({ type: 'REASONING_STEP', step: JSON.parse(ev.message) });
                                         } else {
                                             const log = { id: Date.now() + Math.random(), text: ev.type === 'error' ? `[CRITICAL] ${ev.message}` : `[SYSTEM] ${ev.message}`, type: ev.type, time: new Date().toLocaleTimeString([], { hour12: false }) };
                                             addSystemLog(log);
                                             broadcast({ type: 'LOG_ADDED', log });
                                         }
                                     } catch (e) {
                                        // Ignore parse errors on stream
                                     }
                                 }
                             }
                         }
                     }
                 }
             } finally {
                 broadcast({ type: 'REASONING_END', taskId });
             }
         };
         processStream();
      } else {
          const responseData = await response.json();
          setTimeout(() => {
            const log = { id: Date.now() + Math.random(), text: `[SYSTEM] Edge Swarm Task accepted: ${responseData.status}`, type: 'system', time: new Date().toLocaleTimeString([], { hour12: false }) };
            addSystemLog(log);
            broadcast({ type: 'LOG_ADDED', log });
          }, 2500);
      }

    } catch (err) {
      console.error('Trigger Task Error:', err);
      setTimeout(() => {
        const log = { id: Date.now() + Math.random(), text: `[CRITICAL] Edge Swarm Task Failed: ${err.message}`, type: 'system', time: new Date().toLocaleTimeString([], { hour12: false }) };
        addSystemLog(log);
        broadcast({ type: 'LOG_ADDED', log });
        broadcast({ type: 'REASONING_END', taskId });
      }, 2500);
    }

    return Promise.resolve(taskId);
  },

  getRepositories: async () => {
    try {
      const { data, error } = await supabase.from('coding_tasks').select('repository_name, status, created_at').order('created_at', { ascending: false });
      if (error) {
        console.error('Error fetching repositories:', error);
        return [];
      }

      const repoMap = new Map();
      // Single dashboard-side copy of the fleet: `src/lib/fleetRegistry.js`.
      // It used to be an inline literal here that had drifted from the worker's
      // `edge-coder-worker/src/registry.ts` (phantom `axim-robotics-lab`,
      // mis-cased ASGUARD). Parity is asserted in `fleetRegistry.test.js`.
      const STATIC_REGISTRY = FLEET_REGISTRY;

      // Inject static ecosystem registry nodes
      Object.values(STATIC_REGISTRY).forEach(reg => {
        repoMap.set(reg.repo, {
          id: `R-${reg.repo}`,
          name: reg.repo,
          health: 100,
          activeSwarm: false,
          lastPatch: new Date().toLocaleDateString(),
          language: reg.type === 'rust' ? 'Rust' : (reg.type === 'hybrid' ? 'TypeScript' : 'JavaScript'),
          coverage: 'N/A',
          files: 0,
          branches: 0,
        });
      });

      data.forEach(task => {
        if (!repoMap.has(task.repository_name)) {
          repoMap.set(task.repository_name, {
            id: `R-${task.repository_name}`,
            name: task.repository_name,
            health: 100,
            activeSwarm: task.status === 'IN_PROGRESS',
            lastPatch: new Date(task.created_at).toLocaleDateString(),
            language: 'Unknown',
            coverage: 'N/A',
            files: 0,
            branches: 0,
            dependencies: []
          });
        }
      });

      return Array.from(repoMap.values());
    } catch (err) {
      console.error('Exception fetching repositories:', err);
      return [];
    }
  },

  getSecurityIncidents: async () => {
    try {
      const { data, error } = await supabase
        .from('coding_tasks_errors')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching security incidents:', error);
        return [];
      }

      const incidents = data.filter(row => row.context?.actor === 'Asguard_WAF' || row.message?.includes('security'));

      return incidents.map(row => ({
        id: row.id,
        severity: 'CRITICAL',
        type: row.message || 'Security Incident',
        target: row.context?.target || 'axim-core-api',
        path: row.context?.path || 'N/A',
        status: row.status || 'UNRESOLVED',
        time: new Date(row.created_at).toLocaleString(),
        task_id: row.id,
        error_message: row.message,
        created_at: row.created_at
      }));
    } catch (err) {
      console.error('Exception fetching security incidents:', err);
      return [];
    }
  },



  fetchEdgeTelemetry: async () => {
    try {
      const workerUrl = import.meta.env.VITE_INGRESS_URL ? import.meta.env.VITE_INGRESS_URL.replace('/api/v1/ingress', '') : '';
      if (!workerUrl) return null;

      const res = await fetch(`${workerUrl}/api/telemetry/edge-metrics`);
      if (res.ok) {
        return await res.json();
      }
      return null;
    } catch (e) {
      console.warn('Failed to fetch edge telemetry:', e);
      return null;
    }
  },

  fetchIntegrationHealth: async () => {
    try {
      const workerUrl = import.meta.env.VITE_INGRESS_URL ? import.meta.env.VITE_INGRESS_URL.replace('/api/v1/ingress', '') : '';
      if (!workerUrl) return { github: null, llm: null };

      const res = await fetch(`${workerUrl}/api/v1/health/integrations`);
      if (res.ok) {
        return await res.json();
      }
      return { github: null, llm: null };
    } catch (e) {
      console.warn('Failed to fetch integration health:', e);
      return { github: null, llm: null };
    }
  },

  getTelemetryData: async (timeWindow = '7d') => {
    try {
      const workerUrl = import.meta.env.VITE_INGRESS_URL ? import.meta.env.VITE_INGRESS_URL.replace('/api/v1/ingress', '') : '';
      let edgeData = null;
      let integrationHealth = { github: null, llm: null };
      if (workerUrl) {
          try {
             const res = await fetch(`${workerUrl}/api/telemetry/stats?window=${timeWindow}`);
             if (res.ok) {
                 edgeData = await res.json();
             }
          } catch (e) {
             console.warn('Failed to fetch from worker telemetry API. Falling back to DB...', e);
          }
          integrationHealth = await labService.fetchIntegrationHealth();
      }

      // Basic analytical grouping query over api_usage_logs
      const { data: logs, error } = await supabase
        .from('api_usage_logs')
        .select('*')
        .order('created_at', { ascending: true });

      if (error && !edgeData) {
        console.error('Error fetching telemetry:', error);
        return {
          dateLabels: [],
          tokenUsage: [],
          nodeHealth: [
            { name: 'Core LLM Proxy', status: integrationHealth?.llm ? integrationHealth.llm.status : 'Unknown', latency: integrationHealth?.llm ? `${integrationHealth.llm.latencyMs}ms` : '-', color: integrationHealth?.llm ? (integrationHealth.llm.status === 'Nominal' ? 'green' : (integrationHealth.llm.status === 'Degraded' ? 'yellow' : 'red')) : 'red' },
            { name: 'GitHub API Bridge', status: integrationHealth?.github ? integrationHealth.github.status : 'Unknown', latency: integrationHealth?.github ? `${integrationHealth.github.latencyMs}ms` : '-', color: integrationHealth?.github ? (integrationHealth.github.status === 'Nominal' ? 'green' : (integrationHealth.github.status === 'Degraded' ? 'yellow' : 'red')) : 'red' },
            { name: 'Asguard SOC Ingress', status: 'Unknown', latency: '-', color: 'blue' },
            { name: 'Worker Task Locks', status: 'Unknown', latency: '-', color: 'blue' }
          ],
          roiMetrics: { hoursSaved: 0, efficiencyGain: '0%', totalCost: '$0.00', estimatedSavings: '$0.00' },
          logs: [],
          edgeTelemetry: edgeData
        };
      }

    const tokenUsageMap = new Map();
    let totalTokens = edgeData ? edgeData.total_tokens : 0;
    let totalRequests = edgeData ? edgeData.total_events : 0;
    let totalCost = 0;

    if (logs && logs.length > 0) {
      logs.forEach(log => {
         totalRequests++;

         // Extract token expenditures from metadata
         const tokenCount = Number(log.metadata?.tokens) || 0;
         const model = log.metadata?.model;

         if (model === 'deepseek-coder') {
           const inputTokens = Number(log.metadata?.input_tokens) || 0;
           const outputTokens = Number(log.metadata?.output_tokens) || 0;
           const deepCost = (!isNaN(inputTokens) ? (inputTokens / 1000000) * 0.14 : 0) + (!isNaN(outputTokens) ? (outputTokens / 1000000) * 0.28 : 0);
           totalCost += !isNaN(deepCost) ? deepCost : 0;
         } else if (model === 'claude-3-5') {
           const inputTokens = Number(log.metadata?.input_tokens) || 0;
           const outputTokens = Number(log.metadata?.output_tokens) || 0;
           const claudeCost = (!isNaN(inputTokens) ? (inputTokens / 1000000) * 3.00 : 0) + (!isNaN(outputTokens) ? (outputTokens / 1000000) * 15.00 : 0);
           totalCost += !isNaN(claudeCost) ? claudeCost : 0;
         } else {
           const fallbackCost = (!isNaN(tokenCount) ? (tokenCount / 1000) * 0.01 : 0);
           totalCost += !isNaN(fallbackCost) ? fallbackCost : 0;
         }
         totalTokens += tokenCount;

         if (log.created_at) {
             const date = new Date(log.created_at);
             const mm = String(date.getMonth() + 1).padStart(2, '0');
             const dd = String(date.getDate()).padStart(2, '0');
             const dateLabel = `${mm}/${dd}`;

             if (tokenUsageMap.has(dateLabel)) {
                 tokenUsageMap.set(dateLabel, tokenUsageMap.get(dateLabel) + tokenCount);
             } else {
                 tokenUsageMap.set(dateLabel, tokenCount);
             }
         }
      });
    }

    const dateLabels = Array.from(tokenUsageMap.keys());
    const tokenUsage = Array.from(tokenUsageMap.values());

    const hoursSaved = totalRequests;
      const rawEstimatedSavings = (hoursSaved * 80) - totalCost;
      const estimatedSavings = !isNaN(rawEstimatedSavings) ? rawEstimatedSavings : 0;
      const efficiencyGain = hoursSaved > 0 ? '84%' : '0%';

      return {
        dateLabels,
        tokenUsage,
        nodeHealth: [
          { name: 'Core LLM Proxy', status: integrationHealth?.llm ? integrationHealth.llm.status : 'Unknown', latency: integrationHealth?.llm ? `${integrationHealth.llm.latencyMs}ms` : 'N/A', color: integrationHealth?.llm ? (integrationHealth.llm.status === 'Nominal' ? 'green' : (integrationHealth.llm.status === 'Degraded' ? 'yellow' : 'red')) : 'red' },
          { name: 'GitHub API Bridge', status: integrationHealth?.github ? integrationHealth.github.status : 'Unknown', latency: integrationHealth?.github ? `${integrationHealth.github.latencyMs}ms` : 'N/A', color: integrationHealth?.github ? (integrationHealth.github.status === 'Nominal' ? 'green' : (integrationHealth.github.status === 'Degraded' ? 'yellow' : 'red')) : 'red' },
          { name: 'Asguard SOC Ingress', status: 'Active', latency: 'N/A', color: 'green' },
          { name: 'Worker Analytics (Edge)', status: edgeData ? 'Active' : 'Degraded', latency: edgeData ? `${edgeData.average_latency}ms` : 'N/A', color: edgeData ? 'green' : 'yellow' }
        ],
        roiMetrics: {
          hoursSaved,
          efficiencyGain,
          totalCost: `${(!isNaN(totalCost) ? totalCost : 0).toFixed(2)}`,
          estimatedSavings: `${Math.max(0, (!isNaN(estimatedSavings) ? estimatedSavings : 0)).toFixed(2)}`
        },
        logs: logs || [],
        edgeTelemetry: edgeData
      };
    } catch (err) {
      console.error('Exception fetching telemetry:', err);
      return {
        dateLabels: [],
        tokenUsage: [],
        nodeHealth: [
          { name: 'Core LLM Proxy', status: 'Unknown', latency: '-', color: 'red' },
          { name: 'GitHub API Bridge', status: 'Unknown', latency: '-', color: 'red' },
          { name: 'Asguard SOC Ingress', status: 'Unknown', latency: '-', color: 'blue' },
          { name: 'Worker Analytics (Edge)', status: 'Unknown', latency: '-', color: 'blue' }
        ],
        roiMetrics: { hoursSaved: 0, efficiencyGain: '0%', totalCost: '$0.00', estimatedSavings: '$0.00' },
        logs: [],
        edgeTelemetry: null
      };
    }
  },


  fetchRepositoryTree: async (owner, repo) => {
    try {
      const workerBase = import.meta.env.VITE_INGRESS_URL ? import.meta.env.VITE_INGRESS_URL.replace("/api/v1/ingress", "") : "";
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData?.session?.access_token || '';
      const response = await fetch(`${workerBase}/api/v1/github/tree?owner=${encodeURIComponent(owner)}&repo=${encodeURIComponent(repo)}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      if (!response.ok) throw new Error("Failed to fetch tree");
      const data = await response.json();
      return data.tree || [];
    } catch (err) {
      console.error("Error fetching repository tree:", err);
      return [];
    }
  },

  fetchFilePreview: async (owner, repo, path, branch = 'main') => {
    try {
      const url = `https://raw.githubusercontent.com/${owner}/${repo}/${branch}/${path}`;
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`Failed to fetch file: ${response.statusText}`);
      }
      return await response.text();
    } catch (err) {
      console.error('Error fetching file preview:', err);
      return `Error loading file preview: ${err.message}`;
    }
  },

  getAuditLogs: async () => {
    try {
      const { data, error } = await supabase.from('coding_tasks_errors').select('*').order('created_at', { ascending: false });
      if (error) {
        console.error('Error fetching audit logs:', error);
        return [];
      }
      return data.map(row => ({
        id: row.id,
        timestamp: new Date(row.created_at).toLocaleString(),
        actor: row.context?.actor || row.component || 'System Engine',
        action: row.message || 'DEPLOY_SWARM',
        target: row.context?.target || 'axim-core-api',
        status: row.status || 'SUCCESS'
      }));
    } catch (err) {
      console.error('Exception fetching audit logs:', err);
      return [];
    }
  },


  fetchDlqStats: async () => {
    try {
      const ingressUrl = import.meta.env.VITE_INGRESS_URL || '/api/v1/ingress';
      const statsUrl = ingressUrl.replace('/api/v1/ingress', '/api/v1/dlq/stats');

      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData?.session?.access_token || '';

      const response = await fetch(statsUrl, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch DLQ stats: ${response.statusText}`);
      }

      return await response.json();
    } catch (err) {
      console.error('Exception fetching DLQ stats:', err);
      return { status: "error", pending_count: 0 };
    }
  },

  triggerDlqSweep: async () => {
    try {
      const ingressUrl = import.meta.env.VITE_INGRESS_URL || '/api/v1/ingress';
      const actionUrl = ingressUrl.replace('/api/v1/ingress', '/api/v1/dlq/sweep');

      // The sweep is authorised by the operator's Supabase session. Signing it
      // in the browser would require VITE_AXIM_INTERNAL_KEY, which Vite inlines
      // into the public bundle for every visitor — so it is never read here.
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        throw new Error('Operator session required to sweep the dead-letter queue.');
      }

      const payloadBody = JSON.stringify({ action: 'sweep', timestamp: Date.now() });

      const response = await fetch(actionUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`
        },
        body: payloadBody
      });

      if (!response.ok) {
        throw new Error(`Failed to trigger DLQ sweep: ${response.statusText}`);
      }

      const resData = await response.json();

      const log = {
        id: Date.now(),
        timestamp: new Date().toLocaleTimeString(),
        level: 'info',
        agent: 'AXiM Coder Core',
        message: `Operator executed manual DLQ Sweep. Reprocessed ${resData.reprocessed || 0} failed callbacks.`
      };
      addSystemLog(log);
      broadcast({ type: 'LOG_ADDED', log });

      return { success: true, processedCount: resData.reprocessed || 0 };
    } catch (err) {
      console.error('Exception triggering DLQ sweep:', err);
      return { success: false, error: err.message };
    }
  },


  requestPrReview: async ({ owner, repo, prNumber, taskId }) => {
    try {
      const response = await dispatchSupportTask({
        operation: 'PR_CODE_REVIEW',
        repository_owner: owner,
        repository_name: repo,
        pr_number: prNumber,
        task_id: taskId
      });
      return response;
    } catch (err) {
      console.error('Exception requesting PR review:', err);
      throw err;
    }
  },

  mergePR: async (taskId) => {
    try {
      const { error: insertError } = await supabase.from('coding_tasks_errors').insert({
        component: 'Human Operator',
        error_type: 'PR_MERGED',
        message: `Operator merged pull request for task lock ${taskId}`,
        task_id: taskId,
        status: 'SUCCESS'
      });
      if (insertError) {
        console.error("Error appending PR merge trace log", insertError);
      }
    } catch (e) {
      console.error("Exception appending PR merge trace log", e);
    }

    const { error } = await supabase.from('coding_tasks').delete().eq('id', taskId);
    if(error) console.error("Error merging PR / deleting task", error);

    const tasks = await labService.getTasks();
    broadcast({ type: 'TASKS_UPDATED', tasks });
    return Promise.resolve();
  },

  addComment: async (taskId, text) => {
    try {
      const { data: taskData, error: fetchError } = await supabase
        .from('coding_tasks')
        .select('context')
        .eq('id', taskId)
        .single();

      if (fetchError) {
        console.error('Error fetching task for comment:', fetchError);
        return;
      }

      const currentContext = taskData.context || {};
      const comments = currentContext.comments || [];

      const newComment = {
        id: Date.now().toString(),
        text,
        author: 'Human Operator',
        time: new Date().toLocaleTimeString([], { hour12: false })
      };

      const updatedContext = {
        ...currentContext,
        comments: [...comments, newComment]
      };

      const { error: updateError } = await supabase
        .from('coding_tasks')
        .update({ context: updatedContext })
        .eq('id', taskId);

      if (updateError) {
        console.error('Error updating task with comment:', updateError);
        return;
      }

      const tasks = await labService.getTasks();
      broadcast({ type: 'TASKS_UPDATED', tasks });
    } catch (err) {
      console.error('Exception adding comment:', err);
    }
  },


  checkSupportProxyHealth: async () => {
    const startTime = Date.now();
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 2000);

    try {
      const supportApiUrl = import.meta.env.VITE_SUPPORT_API_URL || 'https://support.axim.us.com';
      const healthUrl = `${supportApiUrl.replace(/\/$/, '')}/api/v1/health`;

      const response = await fetch(healthUrl, {
        method: 'HEAD',
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      if (response.ok) {
        return { alive: true, latencyMs: Date.now() - startTime };
      }
      return { alive: false, latencyMs: Date.now() - startTime };
    } catch (error) {
      clearTimeout(timeoutId);
      return { alive: false, latencyMs: Date.now() - startTime };
    }
  }

};
